import { useEffect, useRef, useState, type FormEvent } from "react";
import type { ReassignResult } from "../hooks/useClaws";
import type { OpenClaw } from "../types/openclaw";
import styles from "./ReassignModal.module.css";

type Props = {
  open: boolean;
  claw: OpenClaw | null;
  onClose: () => void;
  onSubmit: (tenantId: string, ownerEmail: string) => Promise<ReassignResult>;
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const ReassignModal = ({ open, claw, onClose, onSubmit }: Props): JSX.Element | null => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (open && !d.open) {
      d.showModal();
      setEmail("");
      setServerError(null);
      setSubmitting(false);
      window.setTimeout(() => inputRef.current?.focus(), 0);
    } else if (!open && d.open) {
      d.close();
    }
  }, [open]);

  if (!open || !claw) return null;

  const trimmed = email.trim();
  const valid = EMAIL_PATTERN.test(trimmed);

  const handleSubmit = async (evt: FormEvent): Promise<void> => {
    evt.preventDefault();
    if (!valid) return;
    setSubmitting(true);
    setServerError(null);
    const result = await onSubmit(claw.tenantId, trimmed);
    setSubmitting(false);
    if (result.ok) {
      onClose();
      return;
    }
    setServerError(result.message);
  };

  const disabled = submitting || !valid;

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      onCancel={(evt) => {
        evt.preventDefault();
        if (!submitting) onClose();
      }}
    >
      <form method="dialog" className={styles.form} onSubmit={handleSubmit}>
        <header className={styles.header}>
          <h2 className={styles.title}>Reassign OpenClaw</h2>
          <p className={styles.subtitle}>
            Transfer ownership of <span className={styles.nameToken}>{claw.name}</span> to a
            customer. They'll see it on their dashboard next time they sign in.
          </p>
        </header>

        <label className={styles.field}>
          <span className={styles.label}>Customer email</span>
          <input
            ref={inputRef}
            type="email"
            className={styles.input}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setServerError(null);
            }}
            placeholder="customer@example.com"
            autoComplete="off"
            disabled={submitting}
            required
          />
          <span className={styles.hint}>
            Must match the email on the customer's Cognito account. Send an invite first
            if they haven't signed up yet.
          </span>
        </label>

        {serverError && <div className={styles.errorBanner}>{serverError}</div>}

        <footer className={styles.footer}>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button type="submit" className={styles.primaryBtn} disabled={disabled}>
            {submitting ? "Reassigning…" : "Reassign"}
          </button>
        </footer>
      </form>
    </dialog>
  );
};
